/**
 * The per-page user guide shown by <PageGuide id="…" />.
 *
 * Content is data only (no Preact), so a test can check that every line has a Hebrew
 * translation. Strings are written in English and translated through tr(), exactly like
 * the rest of the dashboard: the English text is the key.
 *
 * A line may use three marks, rendered by guide.js:
 *   `code`              → inline code (ids, formats, snippets)
 *   [Label](/path)      → a link into the dashboard (or an https:// link, opened in a tab)
 *   **bold**            → emphasis for the control the step is about
 *
 * A section is either `steps` (ordered: do them in this order) or `items` (unordered facts).
 * `kind: "install"` marks the section that explains what to connect, paste or install.
 */
export const GUIDES = {
  overview: {
    title: "Overview: your morning read",
    intro:
      "The Overview answers two questions: what needs a person today, and how the business did over the last 30 days. Nothing here is edited; every tile is a shortcut into the page that does the work.",
    sections: [
      {
        heading: "Work the page top to bottom",
        steps: [
          "**Get set up** is a checklist of what the agent needs before customers meet it, including any [bracketed] gaps the AI left for you to fill in. Each line opens the page that finishes it, and the whole card disappears once every line is done.",
          "**Today** shows appointments today, chats waiting for a person, tasks due, and past visits you have not marked yet. Click any tile to open the list behind it.",
          "Clear the waiting chats in [Inbox](/inbox), then the tasks in [Tasks](/tasks), then mark yesterday's visits **Attended** or **No-show** in [Bookings](/bookings).",
          "Marking **Attended** is what starts the review request and the rebook invitation, so a page of unmarked visits means those messages never go out.",
        ],
      },
      {
        heading: "What the numbers mean",
        items: [
          "**Booked by AI, in value** is what the appointments the agent booked are worth, at each service's price when it was booked; below it, everything booked in the period and what was actually attended. Cancelled visits, no-shows and unpaid holds don't count, and services without a price are left out.",
          "The tiles cover the last 30 days: conversations started, bookings the AI made, chat-to-booking rate, median time to the first reply, the share of inquiries that arrived while you were closed, no-show rate, chats handed to a person, and your AI cost in US dollars.",
          "**Daily activity** draws conversations against bookings per day. A tall left bar with no right bar means people are asking and not booking; read those chats in the inbox.",
          "**Lead funnel** links each stage to that stage's list in [Contacts](/contacts), and **By channel** shows which channel the conversations came from.",
          "**Where leads come from** is the first touch of each new contact: UTM tags and the referrer on the website chat, Click-to-WhatsApp and Messenger ads, and `m.me` links.",
          "The **Revenue** column of **Where leads come from** adds up what each source's new contacts booked. Your own WhatsApp links and QR codes from [Channels](/channels) appear here under their own names.",
          "Many chats **Handed to a person** usually means one question the agent cannot answer. Find it in the inbox and add the answer under [AI agent](/agent) → Knowledge base.",
        ],
      },
      {
        heading: "The page is empty",
        items: [
          "Figures appear only after customers write in. To see it fill up, open [AI agent](/agent) → **Test your agent** and chat with it yourself; those conversations and bookings are real.",
        ],
      },
    ],
  },

  email: {
    title: "Email: your mailbox, sorted",
    intro:
      "Forward the business mailbox here and every email is sorted the moment it arrives. Customers reach the team at once and get a reply written for them; suppliers and invoices wait in a list; notifications, newsletters and spam are filed away. Nothing is deleted, and the original stays in your mailbox.",
    sections: [
      {
        heading: "Connecting Gmail",
        kind: "install",
        steps: [
          "Press **Create my forwarding address** and copy the address it shows.",
          "In Gmail on a computer: **Settings** → **See all settings** → **Forwarding and POP/IMAP** → **Add a forwarding address**, and paste it.",
          "Gmail sends a confirmation to that address. Within a minute this page shows **Gmail is asking you to confirm** with a button: press **Confirm in Gmail**.",
          "Back in Gmail's settings choose **Forward a copy of incoming mail**, keep Gmail's copy in the Inbox, and save. The badge turns to **Receiving** with the first email.",
        ],
      },
      {
        heading: "What the sorting does",
        items: [
          "Each email gets one kind: **New customer**, **Existing customer**, **Supplier**, **Invoice / payment**, **Notification**, **Newsletter / promotion**, **Spam** or **Other**, with a one-line summary.",
          "A sender rule you set wins first. Auto-replies and bounces are recognised from their headers. Everything else is read by a small AI model that only labels the email and never acts on what it says.",
          "**What happens to each kind** decides the rest: **Alert the team** tells the people in [Settings → Alerts](/settings/alerts), **List it** puts it under **To handle**, **File it away** keeps it under **Filtered**.",
          "An email that can't wait until the end of the day (a same-day cancellation, an upset customer, a payment due today) is marked **Urgent**, goes to the top of **To handle** and alerts the team at once, even when its kind is only listed.",
          "A customer's email still under **To handle** after the hours you choose in **Remind the team when a customer waits** brings one reminder, outside quiet hours. A reply sent from here marks it **Done**; when you answer from your own mailbox, mark it **Done** yourself. An email that only says thanks or goodbye is marked **Done** on arrival, with no alert and no reply.",
          "The text of an email is kept for 30 days, the sender, subject and label for 180.",
        ],
      },
      {
        heading: "Replies to customers",
        items: [
          "**Replies to customers** sets what happens with a customer's email. **Draft** (the default): the AI writes a reply from what your agent knows, and it waits in the email for you. **Automatic**: it goes out by itself. **Off**: nothing is written.",
          "Open an email marked **Reply ready**, change anything in the subject or text, and press **Send reply**. **Discard** drops it; **Write a reply with AI** writes a new one.",
          "Automatic replies hold back whenever a person should look: a complaint, a refund or anything the AI flags, an **Urgent** email, one the sorter wasn't sure was a customer's, or a sender who is neither a customer on file nor verified by their email provider (a sender address can be forged). Those stay as drafts; the ones the AI flagged are marked **Check this one before sending**.",
          "Replies go out under your business name, and the customer's answer reaches your customer contact email from [Settings → Business](/settings/business) (or the owner's sign-in address), then comes back here through forwarding.",
        ],
      },
      {
        heading: "Correcting it",
        items: [
          "Open an email, choose what it really is, and under **Next time** pick the address or the whole domain. That sender is sorted that way from then on, without the AI.",
          "Press **Done** when an email is handled. The number on **Email** in the menu is what still waits.",
        ],
      },
    ],
  },
  calls: {
    title: "Call notes: summaries you approve instead of write",
    intro:
      "Upload the recording of a phone or Zoom call and get a summary written in your own template. You read it, correct it, approve it, and turn the follow-ups into tasks. Nothing is sent to the customer.",
    sections: [
      {
        heading: "Using it",
        steps: [
          "Press **Choose a recording** and pick the file. On a phone this opens your recordings, so a call recorded by the phone's recorder app can be picked directly. m4a, mp3, wav, ogg, webm and amr files up to 25 MB are accepted.",
          "The recording shows **Transcribing…**, then **Summarizing…**, then **Ready to review**, usually within a minute or two. The page updates by itself.",
          "Open it, check the contact, and read the summary. Change anything in the text; the transcript is under **Transcript** if you want to check a detail.",
          "Tick the follow-ups that should become tasks and press **Approve summary**. Ticked follow-ups become tasks on the contact in [Tasks](/tasks), due on the date said in the call or tomorrow at 10:00.",
        ],
      },
      {
        heading: "Good to know",
        items: [
          "Most recorder apps put the other side's number in the file name, so the contact is linked by itself when that number is one of your [Contacts](/contacts). Otherwise search for the contact in the recording.",
          "**Summary template** sets the sections every summary follows, in your own words. Left empty, summaries cover what was discussed, topics, decisions, follow-ups and details to update in the customer's file.",
          "The summary only says what was said in the call; it adds no advice of its own. Automatic transcription can mishear names and numbers, so check them before approving.",
          "A recording that **Failed** shows why. Press **Retry** once the reason is fixed.",
          "The audio is deleted after 30 days. The transcript and summary stay until you press **Delete recording**, which removes everything.",
          "Recording calls, and telling the other side, is your business's responsibility.",
        ],
      },
    ],
  },
  inbox: {
    title: "Inbox: every channel in one thread list",
    intro:
      "WhatsApp, Instagram, Messenger and your website chat arrive in this one list. The AI answers on its own, around the clock; your team steps in only where a person is needed.",
    sections: [
      {
        heading: "Finding the chat you want",
        items: [
          "Start with **Needs action now**, ordered by the oldest unanswered request. **Waiting for customer**, **Response overdue** and **Snoozed** keep the rest separate. Filter by **Assigned to me** or **Unassigned**; **All**, **Needs a person**, **AI handling** and **Closed** remain available.",
          "The search box matches a name or a phone number. The list refreshes by itself every few seconds, so a new message appears without reloading.",
          "Each row shows the owner and waiting time. Owners and admins set a response target in elapsed minutes, including closed hours. In a human chat, use **Waiting on** to override reply detection, or **Snooze** to defer it. A new customer message wakes it; failed sends and automated holding replies never clear the waiting clock.",
        ],
      },
      {
        heading: "Replying, and handing the chat back",
        steps: [
          "Open a chat and read the thread. Messages are labelled **AI**, **Team** or **Automated**, so you can see who said what.",
          "With **How replies go out** set to **Hold for approval** under [AI agent](/agent), each reply waits above the reply box, marked **Reply waiting**. Edit it if you like and press **Send**, or **Discard**. A booking, change or cancellation the agent wants waits with it and happens only when you send. A customer who only says thanks or goodbye gets the agent's one-line answer directly, with nothing to approve.",
          "Press **Take over** to stop the AI on this one conversation. Simply typing a reply and sending it also takes it over, so you cannot talk over the agent by mistake.",
          "Write in the box at the bottom. `Enter` sends, `Shift+Enter` starts a new line.",
          "When you are done, press **Return to AI** so the agent handles the rest of the conversation, or **Close** to file it away. A closed chat reopens by itself if the customer writes again.",
        ],
      },
      {
        heading: "The customer panel on the side",
        items: [
          "**Stage** moves the contact along the funnel, and choosing **lost** asks you why, which is what the weekly review learns from.",
          "**Owner** assigns the contact to a team member; they then see it under **Mine** in Contacts and Tasks.",
          "**Agreed to receive offers** records marketing consent. Only contacts with it on can be included in a campaign, and the date and source of the consent are kept.",
          "**Notes** are for your team and are never sent to the customer. Press **Save notes** to keep them.",
          "**Tasks** adds a follow-up with one of the ready-made due dates, and **Appointments** lists this customer's bookings.",
        ],
      },
      {
        heading: "Worth knowing",
        items: [
          "Voice notes are transcribed automatically and marked **Voice note, transcribed**; the words are speech-to-text, so read names and numbers with care.",
          "A message marked **not delivered** carries the channel's own error in its tooltip. On WhatsApp this is usually the 24-hour rule: once a customer has not written for 24 hours, only an approved template can reach them.",
          "A chat that was handed over shows the reason at the top, which is the fastest way to see what the agent was missing.",
        ],
      },
    ],
  },

  tasks: {
    title: "Tasks: the follow-ups you promised",
    intro:
      "Everything your team said it would do: call back, send a price, chase a quote. Tasks are what the sidebar badge counts, and what the daily brief reminds you about.",
    sections: [
      {
        heading: "How to work it",
        steps: [
          "Start on **Mine**; switch to **Everyone** to see the whole team's open tasks.",
          "Work the groups in order: **Overdue** (shown in red), **Today**, **Tomorrow**, **This week**, **Later**.",
          "The customer's name under each task opens their conversation, so you can see the promise in context before you act on it.",
          "Press **Done** when it is finished. The task leaves the list and the sidebar badge drops.",
        ],
      },
      {
        heading: "Adding a task",
        items: [
          "Tasks are added from a conversation: open it in [Inbox](/inbox), and use **New task** in the customer panel, for example \"Call back about the price\".",
          "Pick a due date with one of the chips (later today, tomorrow morning, next week) instead of typing a date.",
          "A task belongs to the contact, so it travels with them and stays visible to whoever owns them.",
        ],
      },
    ],
  },

  bookings: {
    title: "Bookings: the week's diary",
    intro:
      "One week at a time, in your business time zone. Appointments the AI booked and ones your team entered by hand sit in the same list.",
    sections: [
      {
        heading: "Moving around and booking by hand",
        steps: [
          "Use **← Previous**, **This week** and **Next →** to change the week. The dates and time zone in use are printed under the title.",
          "Press **New booking**, choose a service, then a day: the free start times are worked out from the staff hours, the service length and buffer, your minimum notice, and any linked Google or Outlook calendar.",
          "Pick a time, add the customer's name, mobile and notes, and press **Book**. With a mobile number the customer gets a confirmation on WhatsApp right away and the reminder the day before. If no times appear, the day is full or outside the hours set in [Services & staff](/services).",
        ],
      },
      {
        heading: "Before and on the day",
        steps: [
          "The day before, the reminder asks the customer to confirm, with buttons. **Customer confirmed** appears once they tap **I'll be there**; **Not confirmed yet** marks the ones worth a call. Tapping **Reschedule** or **I can't make it** goes to the AI agent, which offers another time first.",
          "When the customer walks in, press **Arrived**. If an earlier visit has a summary, they get it on WhatsApp with a link to their progress page, every visit with what was done.",
        ],
      },
      {
        heading: "After an appointment",
        steps: [
          "For a past appointment, press **Attended** or **No-show**. This is the only way the platform knows what happened.",
          "**Attended** opens the **Visit summary**: type your notes, press **Draft the customer's summary with AI**, check it and **Approve summary**. Your notes stay internal; the customer sees only the approved summary, at their next check-in and on their progress page.",
          "**Attended** is what triggers the review request and, after the interval you set per service, the invitation to book again.",
          "**No-show** feeds the no-show rate on the Overview, and is the number to watch when deciding whether to ask for a deposit.",
        ],
      },
      {
        heading: "The other buttons",
        items: [
          "**Copy invite link** copies the customer's own appointment page: add to calendar, an `.ics` file, and the joining link for an online service. Paste it into any chat.",
          "**Reschedule** moves an upcoming confirmed booking: pick a free time from the week of the booking, or press **Next →** for later weeks. The same staff member keeps it when they are free, the linked calendar and the reminder move with it, and the customer's invite link shows the new time. If someone took the time a moment earlier, you are told and the booking stays where it was.",
          "**Cancel** frees the slot and, if the waitlist is on, offers it to the customers who were waiting for it.",
          "**awaiting deposit** means the slot is held while the customer pays. An unpaid hold is released after 30 minutes.",
          "**Test booking** is one the agent made in the test chat on the AI agent page. It holds the time but nobody is coming: it has no **Arrived**, **Attended** or **No-show**, sends nothing, and is released once its time passes.",
          "The **Waitlist** card at the bottom lists who is waiting, for what, and whether the slot was already offered to them. **Remove** takes someone off.",
        ],
      },
      {
        heading: "What makes this page accurate",
        kind: "install",
        items: [
          "Staff hours and service lengths come from [Services & staff](/services). Without them the agent has nothing to offer.",
          "Connect your Google or Outlook calendar in [Settings → Integrations](/settings/integrations) so busy events block booking and new appointments land in the staff member's calendar.",
          "Deposits need a Grow account, connected in [Settings → Integrations](/settings/integrations); the amount is set per service.",
        ],
      },
    ],
  },

  contacts: {
    title: "Contacts: everyone who ever wrote to you",
    intro:
      "A contact is created the first time someone messages any of your channels. This page is the funnel: who is new, who booked, who became a customer, and who was lost.",
    sections: [
      {
        heading: "Filtering the list",
        steps: [
          "The first row of chips filters by stage, and each chip carries its own count. The funnel on the Overview links straight into these filters.",
          "The second row filters by owner: **Anyone's**, **Mine**, **Unassigned**. Unassigned contacts are the ones nobody is looking after.",
          "Search by name, phone or email, and click any row to open that contact's most recent conversation.",
        ],
      },
      {
        heading: "Reading the columns",
        items: [
          "**Channel** also shows where the contact came from, for example an ad campaign or an `m.me` link.",
          "**Stage** carries the badges that matter before marketing: **offers ok** means consent was recorded, **opted out** means they asked to stop and no campaign will ever reach them.",
          "A lost contact shows the reason underneath, which is what the agent's weekly review reads.",
          "**Next task** turns red when a follow-up is overdue, and **Last message** is how long ago they last wrote.",
        ],
      },
      {
        heading: "Changing a contact",
        items: [
          "Stage, owner, consent, notes and tasks are all edited in the customer panel of the contact's conversation in [Inbox](/inbox); this page is the list view.",
        ],
      },
    ],
  },

  services: {
    title: "Services & staff: what can be booked, and when",
    intro:
      "This page is the source of truth for availability. The agent quotes from the services here, and offers only times that the staff hours and calendars here allow.",
    sections: [
      {
        heading: "Adding a service",
        steps: [
          "Press **Add service** and give it a name and a description. The description is what the agent reads when it decides which service to recommend, so write it for a customer, not for your books.",
          "Set **Duration** and, if you need cleaning or travel time, **Buffer after**. Both are used to work out free slots.",
          "Set a **Price**, or leave it empty: an empty price makes the agent say the price is on request instead of inventing one.",
          "Choose **Where it happens**. For **Online**, the confirmation carries a joining link: the service's own link, otherwise the link of whoever takes the appointment, otherwise your default room in [Settings → Business](/settings/business).",
          "Optional: **Deposit to book** holds the slot until the customer pays, and **Invite to book again after (days)** sets the rebook interval (28 for a haircut, 180 for a cleaning).",
          "Under **Performed by**, tick who can do it. Tick nobody and any staff member can.",
          "Leave **Bookable** on to publish it. **Hide service** keeps the history but takes it out of the agent's offer.",
        ],
      },
      {
        heading: "Packages and punch cards",
        steps: [
          "Press **Add package**, name it as customers will see it, set the number of sessions, the price and, if it expires, for how many days. Pick the services it can be used for, or none for any service.",
          "With Grow connected under [Settings → Integrations](/settings/integrations), the agent offers packages in the chat and sends a payment link; the sessions become available as soon as it is paid.",
          "Sold one at the desk? **Record a sale** with the customer's phone, and the sessions are available at once.",
          "When a customer with sessions left books a service the package covers, the booking uses one session instead of asking for a deposit. Cancelling gives the session back; a no-show keeps it used.",
        ],
      },
      {
        heading: "Staff, rooms and hours",
        steps: [
          "Press **Add staff member** for each person, chair or room that can be booked separately.",
          "Fill the weekly hours in the format `09:00-13:00, 16:00-19:00`, and leave a day empty to stay closed.",
          "Under **Calendar**, pick the Google or Outlook calendar to sync: busy events in it block booking, and new bookings are written into it.",
          "**Meeting room** is that person's own Zoom, Teams or Meet link, sent to the customer when they book an online service.",
        ],
      },
      {
        heading: "What to connect first",
        kind: "install",
        items: [
          "The calendar list is empty until a Google or Outlook calendar is connected in [Settings → Integrations](/settings/integrations). Connect it there once, then come back and pick a calendar per staff member.",
          "A deposit needs the Grow payment account connected in [Settings → Integrations](/settings/integrations). Until it is, leave the deposit field empty.",
          "A rebook interval here does nothing until invitations are switched on under [AI agent](/agent) → After the visit.",
          "Holidays and one-off closures are not set here: put them in [Settings → Business](/settings/business) → Closed dates.",
        ],
      },
    ],
  },

  agent: {
    title: "AI agent: how it talks, what it knows, when it reaches out",
    intro:
      "Everything the agent says comes from this page. Edit a field, press **Save agent**, then try it in the test chat on the right. Nothing is saved until you press the button.",
    sections: [
      {
        heading: "Changing it by chat",
        steps: [
          "**Update by chat**, at the top of the page, takes plain words: \"ask for a 100 shekel deposit on colour appointments\", \"make the tone warmer\", \"customers keep asking about parking, it is free behind the building\".",
          "The assistant writes the setting and shows it as a card with the value you have now next to the one it suggests. **Apply** saves it, **Dismiss** drops it, and nothing reaches a customer before you press Apply.",
          "It can propose every setting on this page except the model, and it can add, rewrite or remove a knowledge entry. For services, prices, staff hours, channels or billing it tells you which page to go to instead.",
          "Save what you have been typing in the form first: applying a change refreshes the fields with what is now saved.",
          "If it tells you a setting has changed since it suggested something, you or a colleague edited it meanwhile. Ask again and it works from the new value.",
        ],
      },
      {
        heading: "Persona and judgment",
        steps: [
          "**Agent is answering customers** is the master switch. Off, every new chat goes to your team instead.",
          "Set the **Agent name**, **Default language** and **Tone of voice**; the tone is free text, for example \"warm and professional, short sentences\".",
          "**About the business** is who you are and who you serve. **Sales script and policies** is the part you will keep coming back to: how to qualify, what to recommend, your cancellation and deposit rules, and anything the agent must always or never say.",
          "**Also hand off to a person when…** takes one rule per line, for example \"the customer asks about group events\".",
          "**Model** and **Effort** trade cost against judgment: Opus for the trickiest sales conversations, Haiku for simple FAQ and booking, higher effort thinks longer before answering.",
        ],
      },
      {
        heading: "Your Claude API key",
        kind: "install",
        items: [
          "Your agent thinks with Claude, Anthropic's AI model. During the free trial it runs on our key, up to $5 of usage for the whole trial, and on every plan that usage is included in the plan price.",
          "There is nothing to set up. If you would rather Anthropic billed you directly, add your own key in the **Claude API key** card on this page.",
          "Create it at `console.anthropic.com` under **API keys**, after loading credit onto that account, and paste it here. We check it with Anthropic before saving, store it encrypted, and afterwards show only its last four characters.",
          "Only the business owner can add, replace or remove the key. **Remove key** hands the agent back to whatever your plan and trial cover, which may mean it stops answering.",
          "With no key able to pay, the agent stops answering and new chats arrive under **Needs a person**, so your team carries on in the inbox as usual.",
        ],
      },
      {
        heading: "Messages the agent sends on its own",
        items: [
          "**Reminders**: hours before the appointment, comma-separated, for example `24, 2`.",
          "**Follow-ups**: chases a lead who went quiet before booking, after the delay you set, up to the number of times you allow.",
          "**After the visit**: a review request (put your Google review link in) and an invitation to book again. Both start from the moment you mark an appointment **Attended** in [Bookings](/bookings).",
          "**Score question**: asks the customer how likely they are to recommend you, from 0 to 10. A reply of 0 to 6 hands the chat to your team with an alert and a call-back task; the review request still goes to everyone, whatever they scored. The score shows on [Overview](/).",
          "**Waitlist**: when no suitable time is free the agent offers a place in the queue, and a freed slot goes to up to two matching customers, longest waiting first.",
          "**Automated message guardrails**: **Pause all automated messages** stops everything the platform starts by itself, quiet hours hold the non-urgent ones until morning, the daily limit caps them, and the closed-days switch holds them on Shabbat, holidays and your closed dates. Replies to a customer who just wrote are never held.",
        ],
      },
      {
        heading: "What the agent knows",
        steps: [
          "**Knowledge base**: press **Add entry** for a question-and-answer pair, like parking, preparation before a treatment, or what is included. The agent answers from these facts only, and hands off when it has none.",
          "**Documents & links** → **Upload document** for a price list, a treatment guide or your policy sheet; we pull the text out and the agent answers from it.",
          "**Add a link** for your website, menu or price page. The page and the pages it links to are read now, and re-read every week when **Re-read this page every week** is on, so a change on your site reaches the agent by itself.",
          "Press **Edit** on a document to see exactly what the agent reads, fix anything the conversion got wrong, and delete what customers never ask about. Turn **The agent uses this** off to park a document without deleting it.",
          "If you see the over-budget warning, the agent cannot hold all of it at once: switch off what it does not need.",
        ],
      },
      {
        heading: "Weekly review and the test chat",
        items: [
          "**Weekly review** reads the chats that needed a person and the leads that were lost, and suggests what to add. **Accept** writes it into the knowledge base or the instructions, **Dismiss** drops it. Fill in anything in `[square brackets]` first.",
          "**Test your agent** talks to the agent exactly as a customer would, but its bookings are test bookings: they hold the time on the Bookings page, marked **Test booking**, and never reach your Google or Outlook calendar, send a reminder, a confirmation or a webhook, or count on the Overview. They are released once their time passes; **Clear test bookings** frees them sooner. Save your changes before testing, or you will be talking to the old version.",
          "A good first message asks the price of one of your services and for a slot, for example: \"Hi, how much is [your service], and is there a slot tomorrow morning?\" The test chat suggests one with your own service.",
        ],
      },
      {
        heading: "WhatsApp templates you need to prepare",
        kind: "install",
        items: [
          "WhatsApp only allows free text within 24 hours of the customer's last message. Outside that window a reminder, follow-up, review request, rebook nudge or waitlist offer can only go out as an approved template, which is why those fields ask for a template name. For reminders you don't need to write one: when WhatsApp is connected with **Connect WhatsApp**, a standard reminder template is sent to Meta for you and used as soon as it is approved, usually within minutes. A customer WhatsApp can't reach gets the reminder by email when they gave an address.",
          "Create each template in Meta Business → **WhatsApp Manager** → **Message templates** → **Create template**, as category **Utility**, with the body variables the field describes (for a reminder: `{{1}}` name, `{{2}}` service, `{{3}}` time).",
          "Once Meta approves it, type the template's exact name into the matching field here and set **Template language** to the template's language code, for example `he`.",
          "No template means no message outside the 24-hour window; the agent still answers everyone who writes in.",
        ],
      },
    ],
  },

  channels: {
    title: "Channels: connecting WhatsApp, Instagram, Messenger and your site",
    intro:
      "A channel is a place customers can write to you. The website chat is ready the moment you sign up; the Meta channels are connected here.",
    sections: [
      {
        heading: "Connecting WhatsApp through Meta's own flow",
        kind: "install",
        steps: [
          "Have ready: the Facebook account that manages your business, and a phone number that can receive an SMS or a call. A number already live in a personal WhatsApp app cannot be used as it is.",
          "Press **Connect WhatsApp**, then **Continue with Facebook**, and sign in with that account. Allow the popup; a blocker that stops `connect.facebook.net` breaks the flow.",
          "In Meta's windows, choose or create the WhatsApp Business Account and the business profile, then enter the code Meta sends to the number to verify it.",
          "If the number is used in the **WhatsApp Business app** today and you want to keep using that app, tick the box before you start: your history stays in the app, and a reply you send from the phone shows up in the inbox and pauses the AI on that chat. Leave it unticked and the number moves to the Cloud API and stops working in the app.",
          "When the popup closes the channel appears under **Connected**. Send yourself a message to check it end to end.",
          "If you also use that number for family and friends, answer **Yes** when asked whether you use it for personal chats, or turn on **Personal Shield** on the channel later. The AI then answers only people you haven't saved in the phone and haven't chatted with before; a first message from an unsaved number is checked once, and if it might be personal it waits for you with no reply. **Known numbers** shows what the shield knows: switch a saved patient to **Business** so the AI answers them, or any number to **Personal**. In a chat, **This is personal, stop the AI here** does the same from the inbox.",
        ],
      },
      {
        heading: "Connecting by hand instead",
        kind: "install",
        steps: [
          "Use this when Meta's popup is blocked, or when someone else manages the Meta assets. Open the connect window and choose **Enter the phone number ID and token by hand instead**.",
          "**Phone number ID**: Meta Business → **WhatsApp Manager** → **Phone numbers** → the ID under the number (a long number, not the phone number itself).",
          "**Access token**: a permanent **System User** token with the `whatsapp_business_messaging` permission, created in Meta Business Settings → Users → System users → Generate token.",
          "For **Messenger**, take the **Page ID** from Page settings → About, and a Page access token with `pages_messaging`. For **Instagram**, the professional account ID linked to the Page, and a Page token with `instagram_manage_messages`.",
          "Tokens are stored encrypted and never shown again. When one expires, press **Replace token** on the channel: conversations and history are kept.",
        ],
      },
      {
        heading: "Installing the website chat",
        kind: "install",
        steps: [
          "Press **Copy snippet** under **Website chat**. The snippet already carries your business key; there is nothing to configure.",
          "Paste it into your site's HTML just before the closing `</body>` tag, on every page you want the bubble on.",
          "**WordPress**: Appearance → Theme File Editor → `footer.php`, just before `</body>`, or any \"insert headers and footers\" plugin.",
          "**Wix**: Settings → Custom code → add it to **Body - end**.",
          "**Shopify**: Online Store → Themes → Edit code → `theme.liquid`, just before `</body>`.",
          "**Webflow**: Project settings → Custom code → **Footer code**.",
          "Publish the site, open it in a private window, and write something in the bubble: the conversation should appear in [Inbox](/inbox) within seconds.",
        ],
      },
      {
        heading: "WhatsApp links and QR codes",
        steps: [
          "Press **New link**, name the place it goes (Window sticker, Instagram bio, a flyer), choose where it is placed and write the first message the customer will see typed.",
          "**Copy link** for a bio, a button or an ad; **Download QR** for anything printed. Both open the same short link, so every open is counted.",
          "The chat opens with your message and a code such as `(ref K7M2QX)` at the end. When the customer sends it, the new contact is credited to that link.",
          "Each link shows its opens, the chats it started, how many of them booked and the revenue. The same names appear in [Overview](/) under **Where leads come from**.",
          "Editing a link keeps its address and QR code, so anything already printed keeps working. **Archive** stops a link and its QR code.",
        ],
      },
      {
        heading: "Running the channels",
        items: [
          "**Pause** stops the agent answering on that channel without disconnecting it; **Resume** turns it back on.",
          "**Disconnect** removes the channel. Past conversations stay in the inbox.",
          "The plan sets how many channels can be connected at once; see [Settings → Billing](/settings/billing).",
          "**Platform webhook** is shown to platform administrators only. It is set once in the RELAYZA Meta app, not per business.",
        ],
      },
    ],
  },

  "channels.missed_calls": {
    title: "Missed calls to WhatsApp: connecting your phone system",
    intro:
      "When a call to your business goes unanswered, your phone system tells RELAYZA, and the caller gets a WhatsApp message from your business number within a minute. Their reply goes to the AI agent like any other chat. Turn it on with **Text back missed calls**, then connect your phone system with one of the setups below.",
    sections: [
      {
        heading: "How it decides",
        items: [
          "We wait **Wait before sending** first (60 seconds by default). If the call is answered or you call them back in that time, and your phone system reports it, nothing is sent.",
          "One message per caller per **One message per caller every** window (30 minutes by default), however many times they call.",
          "Withheld numbers, your own numbers, staff alert phones and numbers marked personal are never texted.",
          "Within 24 hours of the customer's last WhatsApp message we send a short free message. Otherwise we send RELAYZA's approved template `relayza_missed_call_followup`, submitted to Meta when you turn this on.",
          "The pause switch on the [AI agent](/agent) page stops it, and it counts toward the daily limit for automated messages. It is sent during quiet hours unless you choose **Wait until quiet hours end**.",
          "When WhatsApp can't be used (not connected, no approved template, the number isn't on WhatsApp), a call-back task due now goes to the contact's owner, or else the business owner, and the team gets an alert.",
        ],
      },
      {
        heading: "Connecting Kavkom",
        kind: "install",
        steps: [
          "In Kavkom, open **Advanced**, then **Integrations**, then **Webhook**.",
          "Paste the **Kavkom** URL from this card as the endpoint.",
          "Under **Headers**, add `Authorization` with the value `Bearer` followed by a space and your secret.",
          "Under **Call types**, tick live inbound calls and the call records (CDR) sent after each call. Tick outbound calls too if a call-back you make during the wait should cancel the message.",
          "Press Kavkom's test button, then make a real test: call your business number from a mobile and don't answer. The call shows under **Latest calls** within a minute.",
        ],
      },
      {
        heading: "Connecting through Zapier",
        kind: "install",
        steps: [
          "Make a Zap whose trigger is your phone system's missed-call event. If your system has no Zapier app, have it call **Webhooks by Zapier** with **Catch Hook**.",
          "Add the action **Webhooks by Zapier** with **POST**. URL: the first URL on this card. Payload type: `json`.",
          "Under **Data**, map `caller` to the caller's number and `call_id` to the call's id. Optionally add `called`, `occurred_at`, and `provider` set to `zapier`.",
          "Under **Headers**, add `Authorization` with the value `Bearer` followed by a space and your secret.",
          "Turn the Zap on, press **Send test event** on this card to see the message, then try a real missed call.",
        ],
      },
      {
        heading: "Connecting through Make",
        kind: "install",
        steps: [
          "Make a scenario that starts with your phone system's missed-call module, or with **Webhooks** and **Custom webhook** for your phone system to call.",
          "Add **HTTP** with **Make a request**. URL: the first URL on this card. Method: `POST`. Body type: **Raw**, content type `JSON (application/json)`.",
          "Request content, with your phone system's values mapped in: `{\"call_id\":\"…\",\"caller\":\"…\",\"called\":\"…\",\"status\":\"missed\",\"occurred_at\":\"…\",\"provider\":\"make\"}`",
          "Add the header `Authorization` with the value `Bearer` followed by a space and your secret.",
          "Run the scenario once, then check **Latest calls** on this card.",
        ],
      },
      {
        heading: "The JSON to send",
        items: [
          "A `POST` with `Content-Type: application/json`, and either `Authorization: Bearer <secret>` or `X-Relayza-Signature: sha256=<hex>`, the HMAC-SHA256 of the raw body with your secret.",
          "`{\"call_id\":\"1234-abcd\",\"caller\":\"052-123-4567\",\"called\":\"03-555-0101\",\"status\":\"missed\",\"occurred_at\":\"2026-10-22T09:15:00+03:00\",\"provider\":\"zapier\"}`",
          "Only `caller` is required (`from` works too). Send `call_id` so a retried delivery never sends twice. Israeli numbers can be local (`052-123-4567`) or international (`+972521234567`).",
          "`status` is `missed` by default, and `no_answer`, `busy` and `voicemail` count as missed. Send `answered` or `returned` for the same caller to cancel a message still waiting.",
          "A wrong or missing secret gets `401` and nothing is recorded. **Make a new secret** stops the old one at once.",
        ],
      },
    ],
  },

  campaigns: {
    title: "Campaigns: an approved template to people who said yes",
    intro:
      "A campaign sends one approved WhatsApp template to a group of contacts, then reports what happened. It is for opening hours, a seasonal offer or an announcement, not for a conversation.",
    sections: [
      {
        heading: "Before your first campaign",
        kind: "install",
        steps: [
          "Connect a WhatsApp number under [Channels](/channels). Campaigns run on WhatsApp only.",
          "Create the template in Meta Business → **WhatsApp Manager** → **Message templates** → **Create template**, category **Marketing**, in the language you will send.",
          "Write the body with numbered variables where the personal bits go, for example \"Hi {{1}}, our Passover hours are…\", and submit it. Meta usually answers within a few hours.",
          "Once it is approved it appears in the **Template** dropdown here. If the list is empty you can still type the name and language exactly as approved.",
          "Collect consent as you go: the agent records it in chat, and you can tick **Agreed to receive offers** in a conversation's customer panel. Contacts without it are never included.",
        ],
      },
      {
        heading: "Sending one",
        steps: [
          "Press **New campaign** and name it for yourself, for example \"Passover opening hours\".",
          "Choose the template and language, then map each variable: **Customer's name** (with a fallback for when you do not know it) or **Fixed text**.",
          "Choose the audience with the stage chips, and optionally only people who wrote in the last N days. The line underneath counts how many opted-in contacts match; a count of zero cannot be sent.",
          "Press **Send now**, or set a date and time and press **Schedule**. **Save draft** keeps it for later.",
          "Messages go out in batches inside the allowed hours: Sunday to Thursday 09:00–20:00 and Friday 09:00–14:00. Anyone replying \"הסר\" or STOP is removed from marketing automatically.",
        ],
      },
      {
        heading: "Reading the result",
        items: [
          "**Segments** above the campaigns list are groups that update themselves, such as customers who haven't visited in 90 days. Start from one of the suggestions or press **New segment**; the editor counts as you change the rules and lists the first people who match.",
          "Each segment shows who matches and who **can receive campaigns**: only contacts on WhatsApp who agreed to receive offers. **Campaign** on a segment opens a new campaign with it as the audience, and the campaign keeps those rules even if you edit the segment later.",
          "Click a campaign to see **sent**, **failed** and **skipped** counts, and the per-contact reason under **Not delivered**.",
          "**Skipped** is usually a missing phone number, a contact who opted out in the meantime, or a duplicate.",
          "**Cancel campaign** stops a scheduled or in-flight send; anything already delivered cannot be recalled.",
          "A customer who replies lands in [Inbox](/inbox) as an ordinary conversation, and the agent takes it from there.",
        ],
      },
    ],
  },

  "settings.business": {
    title: "Settings → Business: hours, booking rules and details",
    intro: "The facts the agent repeats and the rules it books by. Press **Save** at the bottom; nothing applies until you do.",
    sections: [
      {
        heading: "Filling it in",
        steps: [
          "**Business name** is what the agent calls you. **Time zone** must be an IANA name such as `Asia/Jerusalem`, and every time on every page is shown in it.",
          "**Address and contact details** is what the agent may share when a customer asks how to get there.",
          "**Website** may be shared with customers, and can be read as a document under [AI agent](/agent) → Documents & links.",
          "**Default meeting room** is the Zoom, Teams or Meet link used for online services when neither the service nor the staff member has one of their own.",
        ],
      },
      {
        heading: "The three booking rules",
        items: [
          "**Minimum notice (minutes)**: how soon from now the agent may book. 120 means nothing in the next two hours.",
          "**Book up to (days ahead)**: how far into the future the diary is open.",
          "**Offer start times every (min)**: the grid of start times offered, for example 15 or 30 minutes.",
          "**Closed on Israeli public holidays**: on by default for a business in Israel, and filled in every year from the calendar: Rosh Hashanah, Yom Kippur, Sukkot, Shemini Atzeret, Passover (first and seventh day), Independence Day and Shavuot. Untick a holiday you open on, or switch it off altogether. Holiday eves and the intermediate days are not included; add them below if you close on them.",
          "**Closed dates**: one `YYYY-MM-DD` per line for vacations and one-off closures. The agent offers nothing on those days, and automated messages can be held on them from [AI agent](/agent) → guardrails.",
        ],
      },
    ],
  },

  "settings.team": {
    title: "Settings → Team: who can get in, and how much they can do",
    intro: "Each person gets their own sign-in, so the inbox shows who replied and tasks can be assigned.",
    sections: [
      {
        heading: "Adding someone",
        steps: [
          "Fill in their name and email under **Add a team member** and choose a role: **Agent** works the inbox, bookings and tasks; **Admin** can also change settings.",
          "Press **Add**. They get an email with a link to choose their own password, valid for 7 days. Until they use it, the list shows **Invite pending** and **Resend invite**.",
          "If email can't go out, the link is shown in the box instead: copy it and send it to them yourself.",
          "**Remove** revokes access immediately. The owner cannot be removed, and their past replies stay in the inbox.",
        ],
      },
      {
        heading: "Worth knowing",
        items: [
          "Your plan caps how many team members you can have; see [Settings → Billing](/settings/billing).",
          "Only the owner can change the plan or connect the payment account.",
        ],
      },
    ],
  },

  "settings.alerts": {
    title: "Settings → Team alerts: being told without watching the screen",
    intro: "Alerts reach your team on WhatsApp and by email when a chat needs a person, plus one short brief every morning.",
    sections: [
      {
        heading: "Setting it up",
        steps: [
          "**Staff phone numbers**: up to three, comma-separated, for example `+972 50 123 4567`. A number starting with `0` is read as Israeli.",
          "**Staff emails**: up to five, comma-separated. They receive the same alerts.",
          "**Brief time**: the hour the daily brief is sent, with today's appointments, chats waiting, tasks due and yesterday's results.",
          "Press **Save**, then use the preview on this page to see the brief as it would look right now, and **Recent alerts** to check what has gone out.",
        ],
      },
      {
        heading: "The template the WhatsApp alert needs",
        kind: "install",
        items: [
          "Without a template, an alert can only reach a phone that wrote to your business number in the last 24 hours, so in practice it will not arrive.",
          "Create a WhatsApp **Utility** template in Meta Business → WhatsApp Manager → Message templates, with one variable `{{1}}` between fixed text, for example `RELAYZA alert: {{1}} Details in the dashboard.` Meta rejects a body that starts or ends with a variable.",
          "Put its exact name in **Alert template**. It is sent in the template language set on the [AI agent](/agent) page.",
        ],
      },
    ],
  },

  "settings.integrations": {
    title: "Settings → Integrations: calendar, payments, CRM, webhooks and Claude",
    intro: "Each block here is independent: connect only what you use. Nothing on this page is needed for the agent to answer customers.",
    sections: [
      {
        heading: "Google Calendar",
        kind: "install",
        steps: [
          "Press **Connect Google Calendar** and sign in with the Google account that owns the calendars. Approve the calendar permission on Google's page; you come back here with a confirmation.",
          "Then open [Services & staff](/services) and pick a calendar for each staff member. Nothing syncs until a calendar is chosen there.",
          "From then on, busy events block booking and every new appointment is written into that calendar, with the customer's details.",
          "A **Reconnect needed** badge means Google stopped accepting the connection, usually because access was revoked in the Google account. Until you reconnect, the agent books against its own schedule only, so events added straight in Google no longer block slots.",
        ],
      },
      {
        heading: "Outlook / Microsoft 365",
        kind: "install",
        steps: [
          "Press **Connect Outlook / Microsoft 365** and sign in with the Microsoft account that owns the calendars: a work or school account, or a personal Outlook.com one. Approve the calendar permission on Microsoft's page; you come back here with a confirmation.",
          "If Microsoft says an administrator has to approve the app, your organisation only lets admins allow new apps. Ask your Microsoft 365 admin to approve RELAYZA, then connect again.",
          "Then open [Services & staff](/services) and pick an Outlook calendar for each staff member, exactly as with Google. A business connects one calendar account at a time: connecting Outlook replaces Google, and each staff member's calendar is picked again.",
          "A **Reconnect needed** badge means Microsoft stopped accepting the connection, usually because access was revoked or the password changed. Until you reconnect, the agent books against its own schedule only, so events added straight in Outlook no longer block slots.",
        ],
      },
      {
        heading: "Deposits with Grow",
        kind: "install",
        steps: [
          "Ask Grow to open API access for your account, and copy the **userId** and the **pageCode** of the payment page they give you.",
          "Paste both here and press **Connect Grow**. Only the business owner can do this.",
          "Grow pointed you to Make instead of opening API access? Choose **Through Make**, build the scenario the panel describes in your own Make account, paste its webhook address, press **Connect Grow** and then **Run a test**. Make only creates each payment link; the money and the payment confirmation go straight between the customer, Grow and us.",
          "Set the deposit amount per service in [Services & staff](/services). Customers then pay by card, Bit, Apple Pay or Google Pay before the slot is confirmed, and an unpaid hold is released after 30 minutes.",
          "A **Test mode** notice means payments are going to Grow's sandbox, not to your account.",
        ],
      },
      {
        heading: "CRM sync",
        kind: "install",
        steps: [
          "**HubSpot**: HubSpot → Settings → Integrations → **Private apps** → create an app with `crm.objects.contacts` read and write, copy its token, paste it here and press **Connect HubSpot**.",
          "**Monday.com**: Monday → your avatar → **Developers** → **My access tokens** for the token, and the board id from the board's URL.",
          "After Monday is connected, map the **Phone**, **Email** and **Stage** columns from the dropdowns; leave one as **Don't sync** to skip it.",
          "From then on every lead becomes a CRM record whose stage is kept up to date, with a timeline note for each booking, cancellation and handoff. A **Sync error** badge means the token or the board changed.",
        ],
      },
      {
        heading: "Webhooks to your own systems",
        kind: "install",
        items: [
          "Add an endpoint URL and the events you want: `*` for everything, or a comma-separated list such as `booking.created, conversation.handoff, contact.stage_changed`.",
          "The signing secret is shown once, when you add the webhook. Copy it then; it cannot be read again.",
          "Every request is signed with HMAC-SHA256 in the `x-signature` header. Verify it with that secret before you act on the payload.",
          "Zapier and Make both accept this as a \"catch hook\" trigger, which is the quickest way to try it.",
        ],
      },
      {
        heading: "Claude and your own tools (API and MCP)",
        kind: "install",
        steps: [
          "Under **API and AI apps (MCP)**, name a key after where it will be used and choose **Read only** or **Read and write**. Only the business owner sees this block.",
          "Copy the key the moment it appears: it is shown once. **Revoke** stops a key at once.",
          "For Claude Code, copy the `claude mcp add` command shown and run it in a terminal. Then ask Claude things like \"how did we do last week?\" or \"book Dana for a cleaning on Sunday at 10\".",
          "A read-only key can see results, bookings, availability, customers and segments. A read-and-write key can also book, cancel and add follow-up tasks. No key can send marketing messages, change billing or manage the team.",
        ],
      },
    ],
  },

  "settings.billing": {
    title: "Settings → Billing: plan, card and invoices",
    intro: "What you are on, what it costs, and what has been charged. Only the business owner can change the plan or the card.",
    sections: [
      {
        heading: "Choosing or changing a plan",
        steps: [
          "The top card shows your current plan and status: trialing, active, past due, unpaid or cancelled.",
          "Switch between **Monthly** and **Yearly** (two months free) and press the button on the plan you want. A plan change takes effect straight away.",
          "**Cancel plan** lets the plan run to the end of the paid period and then stops the agent. **Keep my plan** undoes that while the period is still open.",
        ],
      },
      {
        heading: "Card, invoices and limits",
        items: [
          "**Update card** or **Replace card** opens the secure payment page. A small card check may be charged and credited back on your next bill. We never see your card number.",
          "Tax invoice-receipts are issued by Morning and emailed to the billing address; every charge is listed under **Invoices and charges** with a link to the document.",
          "Each plan includes a number of AI conversations a month; beyond it, extra conversations are billed per conversation and prices exclude VAT.",
          "WhatsApp conversation fees are charged by Meta directly, not by us.",
          "If the agent is paused because of billing, the banner at the top of every page says why; new chats go to your team until it is resolved.",
        ],
      },
    ],
  },

  "settings.account": {
    title: "Settings → Account: your own sign-in",
    intro: "Your personal settings. Nothing here affects the rest of the team.",
    sections: [
      {
        heading: "Changing your password",
        steps: [
          "Type the password you use now. If you have not changed it yet, that is the temporary one you were given.",
          "Choose a new password of at least 8 characters and repeat it underneath.",
          "Press **Change password**. Every other device is signed out, which is what you want if a password was shared.",
        ],
      },
      {
        heading: "Where the rest lives",
        items: [
          "Language (English or עברית) and dark or light mode are at the bottom of the sidebar, and are remembered on this device.",
          "If you have access to more than one business, the picker at the top of the sidebar switches between them.",
        ],
      },
    ],
  },

  setup: {
    title: "Setup: let the AI write your agent",
    intro:
      "Answer a page of questions once, and we draft the agent's instructions, services, opening hours and FAQs from them. You then edit everything before it goes live, so a rough answer is better than an empty one.",
    sections: [
      {
        heading: "Step 1: your answers",
        steps: [
          "Write the way you would explain the business to a new receptionist. Short answers are fine; anything you leave out comes back as a question to fill in.",
          "**What customers can book**: one per line with length and price when you have one, for example \"Colour 2h ₪450\". Leave the price out and the agent will not quote one.",
          "**Policies** and **When must a person take over?** are the two that save you the most work later: cancellation, lateness, deposits, and the subjects the agent must never handle alone.",
          "Have a website? Put it in the box at the top and press **Explore my website**: we read it and fill in the answers it covers, leaving whatever you already wrote. Check them, then fill in what it missed.",
          "Press **Draft my agent** and wait about half a minute. **Save for later** keeps your answers, and **I'll set it up myself** skips to an empty agent you fill in by hand.",
        ],
      },
      {
        heading: "Step 2: check the draft",
        steps: [
          "Read **Worth answering before you go live** first: anything in `[square brackets]` is a gap we refused to guess at.",
          "Fix the opening hours in the format `09:00-13:00, 16:00-19:00`, leaving a day empty for closed.",
          "Untick any service or knowledge entry you do not want created, and correct names, lengths and prices in place.",
          "**Starter pack for your kind of business** shows what comes switched on with the draft: inviting customers back after the usual interval for your trade, the 0 to 10 score question after a visit, and ready-made campaign segments. Untick it to leave those off. Its WhatsApp template texts are ready to copy into WhatsApp Manager.",
          "Decide whether to tick **Let the agent start answering customers now**, then press **Save and finish setup**. **Draft again** rewrites from your answers, a limited number of times.",
        ],
      },
      {
        heading: "Step 3: what to do next",
        kind: "install",
        items: [
          "Test the agent in the chat on [AI agent](/agent). Bookings made there are real.",
          "Connect your WhatsApp number in [Channels](/channels). Until then the agent answers on your website chat only.",
          "Connect your Google or Outlook calendar in [Settings → Integrations](/settings/integrations) so busy times block booking.",
          "Check services, staff and prices in [Services & staff](/services); everything we created is editable there.",
          "This wizard opens by itself until it is finished. Everything in it can be changed later under [AI agent](/agent).",
        ],
      },
    ],
  },

  website: {
    title: "Website: your business online",
    intro:
      "For a business without a site of its own: a one-page website at your own relayza.company address, with online booking and your AI chat built in. Included in every plan.",
    sections: [
      {
        heading: "Already have a website",
        items: [
          "**I have my own website** is at the top of the page. If you gave your website in setup or in [Settings → Business](/settings/business), it's already filled in and confirmed: check it and press **Connect my website**, and your relayza.company address forwards to it.",
          "**Import its content** instead writes a RELAYZA page from your site's facts, with booking and chat built in. **Stop forwarding** undoes the connection.",
          "While it forwards, your lecture and file pages and their QR codes keep working on your relayza.company address.",
        ],
      },
      {
        heading: "Building it",
        steps: [
          "Press **Yes, build my website**. We write the words from your setup answers and the agent's description, in a look made for your kind of business.",
          "Pick the **Address**: we suggest one from your business name. English letters, digits and hyphens; we tell you at once if it's taken.",
          "Choose a **Look** and, if you like, your own **Colour**. The preview on the side shows it straight away.",
        ],
      },
      {
        heading: "Keeping it right",
        items: [
          "Services, prices, opening hours and the address are not typed here: they come from **Services & staff** and **Settings**, so the site always matches what the agent says.",
          "Edit the headline, the about text, the reasons to choose you and the questions freely. **Rewrite with AI** writes them again, a limited number of times; your own edits are unlimited.",
          "**Before you publish** lists what's missing. Tick **Turn on online booking** so the button opens your booking page; without it, the button opens WhatsApp.",
          "**Publish my website** puts it live. After that, saving updates the live site, and **Take offline** hides it again.",
        ],
      },
      {
        heading: "Getting leads from it",
        items: [
          "Tick **Show a contact form on my website** under **Contact form**, then save. The form needs WhatsApp connected in [Channels](/channels), because that's the number we write from.",
          "A sent form becomes a contact with the ad or campaign that brought them, and the AI agent writes to them on WhatsApp within seconds. A new contact hasn't written to you, so this first message needs an approved WhatsApp template (body: `{{1}}` is their name); without one, the lead waits in [Tasks](/tasks) for a person to call. Your team gets an alert either way.",
          "The form asks for consent to updates separately and unticked; only people who tick it can get your campaigns.",
          "Under **Ads and analytics tracking**, paste your Meta Pixel, Google Analytics 4 or Tag Manager id. A sent form counts as a Lead in Meta and `generate_lead` in Google. Mention these tags in your privacy notice.",
          "**Share preview and icon**: upload the image WhatsApp and Facebook show under your link (1200×630, under 300 KB) and the browser-tab icon.",
        ],
      },
      {
        heading: "The Advisor look",
        items: [
          "For pension and insurance agents, financial advisors, accountants and lawyers: pick **Advisor** under **Look**. It opens light, and visitors can switch it to dark.",
          "Under **Practice details**, add up to four credentials, the steps of how you work, a \"what we check\" example card, your licence number and the footer notice, and tick **Show the retirement calculator** if you want it. Each part shows only when it has something in it.",
          "**Rewrite with AI** fills the credentials, steps and checks too, with anything it can't know in [square brackets]. Replace those before you publish; your licence number, the notice and the calculator stay as you set them.",
          "Upload a **Logo** for the header and a **Portrait** for beside About us under **Share preview and icon**.",
        ],
      },
      {
        heading: "Lectures and files",
        steps: [
          "The **Lectures and files** card appears once RELAYZA switches the library on for your business. Press **Upload**, choose the file (PDF, PowerPoint, Word, Excel, PNG or JPEG, up to 25 MB), and say whether it's a lecture (where and when you gave it) or a file you hand out.",
          "Choose **Who can download it**. **For name and number** (the default) asks for a name and a mobile number first: the visitor becomes a WhatsApp contact tagged with that lecture, the AI agent writes to them, your team gets a lead alert, and the download link works once, for ten minutes. **Open download** gives the file to anyone; **Hidden** takes the page down.",
          "Put the item's QR code on your last slide or a flyer: **QR as PNG** or **QR as SVG**, or **Copy link**. It leads to the item's page on your website, so [Overview](/) shows which talk brought which leads.",
          "**Edit** and **Replace file** keep the same page and QR code, so anything already printed keeps working. **Delete** asks you to confirm, and then removes the file and its page.",
        ],
      },
      {
        heading: "Using your own domain",
        steps: [
          "Under **Your own domain**, type the address you want, such as `www.your-business.co.il`, and press **Connect**.",
          "At the company you bought the domain from, add the **CNAME** record the card shows. The optional **TXT** record only speeds up the check.",
          "Press **Check now**, or just come back later: once the badge says **Connected**, your site and its secure certificate are live on that address, and your relayza.company address keeps working.",
        ],
      },
    ],
  },

  admin: {
    title: "All businesses: the platform view",
    intro: "Visible to platform administrators only. One row per business, with usage, plan and AI spend.",
    sections: [
      {
        heading: "Onboarding a business",
        steps: [
          "Fill **Onboard a business** with the business name, the owner's name and email, and the time zone, then press **Create**.",
          "The owner gets an email inviting them to choose a password; nobody sees or sends a password. If that email already had an account, it is added as owner and told so.",
          "The owner signs in and lands in the setup wizard, which drafts their agent from a page of questions.",
          "Until they sign in, the row shows **Invite pending**. **Resend invite** sends a fresh link, for example when the first one expired after 7 days. If email can't go out, the link is shown for you to send yourself.",
        ],
      },
      {
        heading: "Billing and budget per business",
        items: [
          "**Manage** opens plan and trial for that business: set or extend a trial, make it complimentary, or waive the setup fee.",
          "A business paying by card changes its own plan; from here you can still waive its setup fee.",
          "**AI budget…** sets the most AI may cost on that business in a month. When it is reached, new chats go to that business's team instead of the agent.",
          "Every business has a budget by default: on Growth and Pro it is what the plan can earn in a month, so paying customers never meet it; complimentary and Founding get $50. A business on its own Anthropic key has none, and a trial is limited by its $5 credit instead.",
          "The table shows conversations and bookings over 30 days and AI cost this month, which is the fastest way to spot a business that is stuck.",
        ],
      },
      {
        heading: "The Claude API key",
        kind: "install",
        items: [
          "Our own Anthropic key pays for trials, every paid plan and complimentary businesses. It is the Worker secret `ANTHROPIC_API_KEY` in Cloudflare (Workers → whatsapp-schedule → Settings → Variables and Secrets); the **Claude API key** card shows whether it is set.",
          "A business can bring its own key on its AI agent page (owners only), though no plan requires one. Its own key always wins over ours.",
        ],
      },
      {
        heading: "The payment account",
        kind: "install",
        items: [
          "Taking payments needs `MORNING_API_KEY_ID`, `MORNING_API_KEY_SECRET` and `MORNING_PLUGIN_ID` set as Worker secrets in Cloudflare (Workers → whatsapp-schedule → Settings → Variables and Secrets).",
          "When you move to a different Morning account, replace those three secrets first, then forget the saved cards here: they belong to the old account and cannot be charged from the new one.",
          "Every business whose card is forgotten is asked to add it again, so do this deliberately.",
        ],
      },
    ],
  },

  join: {
    title: "Buying a plan",
    intro: "This page turns a plan into a working account: you pay, and the agent is set up with you within three business days.",
    sections: [
      {
        heading: "Filling it in",
        steps: [
          "Choose the plan and whether you pay **Monthly** or **Yearly** (two months free). The table shows exactly what is charged today, including VAT and any one-time setup fee.",
          "Fill in your business and contact details. The email you give is both your sign-in and where invoices are sent.",
          "Add the business number (`ח.פ. / ע.מ.`) if you want VAT-deductible invoices.",
          "Choose a password of at least 8 characters, accept the terms, and press **Continue to secure payment**.",
          "Payment is handled by Morning and Grow; we never see your card number. When it goes through, sign in and the setup wizard opens.",
        ],
      },
      {
        heading: "If something stops you",
        items: [
          "\"This email already has an account\": sign in instead and choose a plan from [Settings → Billing](/settings/billing).",
          "If online payment is not switched on yet, write to `hello@relayza.company` and we will complete the purchase with you.",
        ],
      },
    ],
  },

  auth: {
    title: "Signing in",
    intro: "One account per person, per business.",
    sections: [
      {
        heading: "How to get in",
        steps: [
          "Enter your email and password. If your account was created for you, use the temporary password you were given and change it afterwards under Settings → Account.",
          "Forgot your password? Use **Forgot password?** under the password field: we email you a link that works once, within an hour. Choosing a new password signs you out on every device, the app included.",
          "No account yet? Use **Request early access**, or buy a plan from the pricing page on our website.",
          "The button at the bottom switches the whole dashboard between English and עברית, including right-to-left layout.",
          "On your first sign-in the setup wizard opens and writes your agent from a page of questions.",
        ],
      },
    ],
  },
};

